import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  LayoutDashboard,
  Users,
  UserPlus,
  Briefcase,
  Tags,
  CheckSquare,
  Upload,
  Settings,
  Menu,
  X,
  ChevronLeft,
  LogOut,
  Building2,
  Calendar,
  UserCog,
  ScrollText,
  BarChart3
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "host", "member"] },
  { path: "/visitors", label: "Visitors", icon: UserPlus, roles: ["admin", "host", "member"] },
  { path: "/members", label: "Members", icon: Users, roles: ["admin", "host", "member"] },
  { path: "/followups", label: "Follow-ups", icon: CheckSquare, roles: ["admin", "host", "member"] },
  { path: "/events", label: "Events", icon: Calendar, roles: ["admin", "host", "member"] },
  { path: "/reports", label: "Reports", icon: BarChart3, roles: ["admin"] },
  { path: "/power-teams", label: "Power Teams", icon: Briefcase, roles: ["admin"] },
  { path: "/categories", label: "Categories", icon: Tags, roles: ["admin"] },
  { path: "/import", label: "Import Data", icon: Upload, roles: ["admin"] },
  { path: "/users", label: "Users", icon: UserCog, roles: ["admin"] },
  { path: "/audit-log", label: "Audit Log", icon: ScrollText, roles: ["admin"] },
  { path: "/settings", label: "Settings", icon: Settings, roles: ["admin"] },
];

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, chapter, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const filteredNavItems = navItems.filter(item => item.roles.includes(user?.role || "member"));

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const primaryColor = chapter?.primary_color || "#CC0000";

  // Dynamic branding styles
  const brandingStyles = {
    "--dynamic-primary": primaryColor,
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" style={brandingStyles}>
      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:sticky top-0 left-0 z-40 h-screen
          bg-white border-r border-slate-200 
          transition-all duration-300 ease-in-out
          ${sidebarOpen ? "w-64" : "w-20"}
          ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200">
            {sidebarOpen && (
              <div className="flex items-center gap-3">
                {chapter?.logo_url ? (
                  <img 
                    src={`${API.replace('/api', '')}${chapter.logo_url}`} 
                    alt="Logo" 
                    className="h-8 w-auto object-contain"
                  />
                ) : (
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: primaryColor }}
                  >
                    B
                  </div>
                )}
                <span className="font-display font-bold text-slate-900 truncate">
                  {chapter?.name || "BNI CRM"}
                </span>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hidden lg:flex"
              data-testid="sidebar-toggle"
            >
              <ChevronLeft className={`h-5 w-5 transition-transform duration-300 ${!sidebarOpen ? "rotate-180" : ""}`} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileOpen(false)}
              className="lg:hidden"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {filteredNavItems.map((item) => {
                const isActive = location.pathname === item.path || 
                  (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
                const Icon = item.icon;

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-lg
                      transition-colors duration-200
                      ${isActive 
                        ? "bg-primary/10 text-primary font-medium" 
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                      }
                    `}
                    style={isActive ? { color: primaryColor, backgroundColor: `${primaryColor}15` } : {}}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    {sidebarOpen && <span>{item.label}</span>}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User section */}
          <div className="p-4 border-t border-slate-200">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className={`w-full justify-start gap-3 ${!sidebarOpen && "justify-center px-0"}`}
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback style={{ backgroundColor: primaryColor }} className="text-white">
                      {user?.name?.charAt(0)?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  {sidebarOpen && (
                    <div className="flex-1 text-left truncate">
                      <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                      <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                    </div>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-white/80 backdrop-blur-sm border-b border-slate-200 flex items-center px-4 lg:px-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(true)}
            className="lg:hidden mr-2"
            data-testid="mobile-menu-trigger"
          >
            <Menu className="h-5 w-5" />
          </Button>
          
          <div className="flex-1">
            {chapter?.header_text && (
              <p className="text-sm text-muted-foreground hidden sm:block">{chapter.header_text}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            {!user?.chapter_id && user?.role === "admin" && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate("/settings")}
                className="hidden sm:flex"
              >
                <Building2 className="h-4 w-4 mr-2" />
                Setup Chapter
              </Button>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6">
          <div className="max-w-7xl mx-auto animate-fade-in">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
